﻿import React, { useState, useEffect } from 'react';
import { NuclearMagneticResonanceBenchSheet } from '../models/nuclear-magnetic-resonance-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, NoteType, Entities } from '../models/enums';
import { DocumentationConstants } from '../store/documentation-constants';
import { FieldUpdate } from '../models/field-update';
import { NmrSolventType } from '../models/nmr-solvent-type';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import DocumentLoader from './DocumentLoader';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetData from './BenchSheetData';
import { CrudFunctions } from '../utilities/crud-functions';
import StandardsViewer from './StandardsViewer';
import { NmrInternalStandard } from '../models/nmr-internal-standard';
import NmrSolventsData from './NmrSolventsData';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import NoteViewer from './NoteViewer';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import BenchSheetToolbar from './BenchSheetToolbar';
import DmaFooter from './DmaFooter';

import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import RadioGroup from 'devextreme-react/radio-group';

let key = 1;

//temp
const nmrBenchSheetTypes = [
    { id: 0, name: 'Qualitative' },
    { id: 1, name: 'Quantitative' }
];

export default function NuclearMagneticResonanceBenchSheetForm() {
    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [benchSheet, setBenchSheet] =
        useState<NuclearMagneticResonanceBenchSheet>();

    const [solventTypes, setSolventTypes] =
        useState<NmrSolventType[]>();

    const [internalStandards, setInternalStandards] =
        useState<NmrInternalStandard[]>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>(false);

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    return (
        <div className="pw-content">
            <BenchSheetToolbar title="NMR Bench Sheet"
                subtitle="Nuclear Magnetic Resonance"
                auditRowPointer={benchSheetId} entity={Entities.NmrBenchSheets}
                technologyId={BenchSheetTechnologyType.NuclearMagneticResonance}
                documentationId={DocumentationConstants.HNMRBenchSheet}
                showItemInformation={showBenchSheetInformationDialog}
                showTicketLog={showTicketLogEntryDialog} />

            <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

            <div className="pw-form-row pw-margin-top-1">
                <div className="pw-form-unit">
                    <div className="pw-form-unit-label">NMR Bench Sheet Type</div>
                    <RadioGroup id="nmrBenchSheetType" items={nmrBenchSheetTypes}
                        displayExpr='name' valueExpr='id' layout="horizontal"
                        onValueChanged={onNmrBenchSheetTypeValueChanged}
                        value={benchSheet ?
                            benchSheet.nmrBenchSheetType : undefined} />
                </div>
            </div>

            <div className="pw-margin-top-1">
                <SecondaryBenchSheetInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technology={BenchSheetTechnologyType.NuclearMagneticResonance}
                    headerBgClass="pw-bg-vc-600"
                    title="NMR Instrument" />
            </div>

            <div className="lq-header pw-margin-top-05 pw-margin-bottom-05 aurum-blue-bg">
                <div className="lq-header-title">Weight Data</div>
            </div>
            <div>
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Solvent</div>
                        <SelectBox id="solventId" className="selectbox"
                            placeholder='NA' readOnly={!canEdit}
                            displayExpr='solventName' valueExpr='solventTypeId'
                            dataSource={solventTypes ? solventTypes : []}
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.solventId ?
                                benchSheet.solventId : null} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Solvent Part#</div>
                        <TextBox id="solventPartNumber" className="textbox"
                            readOnly={!canEdit}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={benchSheet && benchSheet.solventPartNumber ?
                                benchSheet.solventPartNumber : undefined} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Solvent Lot#</div>
                        <TextBox id="solventLotNumber" className="textbox"
                            readOnly={!canEdit}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={benchSheet && benchSheet.solventLotNumber ?
                                benchSheet.solventLotNumber : undefined} />
                    </div>
                </div>

                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">QC Standard</div>
                        <SelectBox id="qcStandardId" className="selectbox"
                            placeholder='NA' readOnly={!canEdit}
                            displayExpr='standardName' valueExpr='nmrInternalStandardId'
                            dataSource={internalStandards ? internalStandards : []}
                            onValueChanged={onValueChanged}
                            value={benchSheet && benchSheet.qcStandardId ?
                                benchSheet.qcStandardId : null} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">QC Standard Part#</div>
                        <TextBox id="qcStandardPartNumber" className="textbox"
                            readOnly={!canEdit}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={benchSheet && benchSheet.qcStandardPartNumber ?
                                benchSheet.qcStandardPartNumber : undefined} /></div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">QC Standard Lot#</div>
                        <TextBox id="qcStandardLotNumber" className="textbox"
                            readOnly={!canEdit}
                            onValueChanged={onValueChanged}
                            placeholder='NA'
                            value={benchSheet && benchSheet.qcStandardLotNumber ?
                                benchSheet.qcStandardLotNumber : undefined} />
                    </div>
                </div>
            </div>

            {benchSheet && benchSheet.nmrBenchSheetType === 1 &&
                <div>
                    <NmrSolventsData benchSheetId={benchSheetId} isReadOnly={!canEdit} />
                </div>
            }

            <div className="pw-comp-row-scroll-fixed-grid">
                <NoteViewer height='10rem' title="Bench Sheet Note"
                    noteType={NoteType.Method} parentId={benchSheetId} />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <BenchSheetData parentId={benchSheetId}
                    readOnly={!canEdit} canDelete={true}
                    technology={BenchSheetTechnologyType.NuclearMagneticResonance}
                    imageHandlerTitle="NMR Image"
                    documentType={DocumentType.General} />
            </div>

            <div>
                <div className="lq-header pw-margin-top-05">
                    <div className="lq-header-title">Standards Documents</div>
                </div>

                {/* Right now just have all standards documents listed under generic StandardsDocument type */}
                <DocumentLoader parentId={benchSheet && benchSheet.batchId ? benchSheet.batchId : null} readOnly={true}
                    documentType={DocumentType.StandardsDocument}
                    canDelete={canEdit} />
            </div>

            {/*
                <div>
                    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?
                        benchSheet.batchId : undefined}
                        technologyId={BenchSheetTechnologyType.NuclearMagneticResonance} />
                </div>
                */}


            <DmaFooter />

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="NMR Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    function onNmrBenchSheetTypeValueChanged(e: any) {
        //todo - need to warn before blowing away a bunch of data

        if (!dataLoaded)
            return;

        const currentValue = benchSheet.nmrBenchSheetType;

        if (currentValue === e.value)
            return;
        else if (!currentValue && !e.value)
            return;
        else {
            setBenchSheet({ ...benchSheet, nmrBenchSheetType: e.value });

            const fieldUpdate: FieldUpdate = {
                entity: "NuclearMagneticResonanceBenchSheets",
                field: "nmrBenchSheetType",
                value: e.value,
                rowId: benchSheetId
            };

            updateField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Value");
                });
        }
    }

    function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        if (e.element && e.value) {
            const caller = e.element.id;

            const currentValue = benchSheet[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

                const fieldUpdate: FieldUpdate = {
                    entity: "NuclearMagneticResonanceBenchSheets",
                    field: caller,
                    value: e.value,
                    rowId: benchSheetId
                };

                updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Value");
                    });
            }
        }
    }

    //GET methods

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then((data) => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function getInternalStandards() {
        await CrudFunctions.getData("benchsheets/nmrinternalstandards")
            .then((data) => {
                console.log(data);

                setInternalStandards(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function getSolventTypes() {
        await CrudFunctions.getData("benchsheets/nmrsolventtypes")
            .then((data) => {
                setSolventTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/nmrbenchsheetfield", fieldUpdate);
    }

    async function getAllData() {
        await Promise.all([
            getSolventTypes(),
            getInternalStandards(),
            getBenchSheet()
        ])
            .then(() => setDataLoaded(true));
    }

    //show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            const itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

